import { Injectable } from '@angular/core';
import { delay, from, type Observable, of } from 'rxjs';
import type {
  NotificationDefinitionGetDto,
  NotificationDefinitionPutDto,
} from '../../../../backend/main';
import {
  deleteAdminNewsAsync,
  findNotificationDefinitionAsync,
  listNotificationDefinitionsAsync,
  type NotificationDefinitionListDto,
  updateNotificationDefinitionAsync,
} from '../../../../backend/main';
import type { DtoListService, GetAllRequestParameters, ListPaginatedDTO } from '@pct/ng-core-dto';
import { UNKNOWN_ITEM } from '@pct/ng-core-dto';
import { isNil } from '@pct/ts-typeguard';
import type { FilteredParams } from '../../../common/request/filtered-params';
import { mapSort } from '../../../common/request/map-sort';
import { convertPaginationParams } from '../../../common/utils/table/convert-pagination-params';
import type { NotificationFilter } from '../list/header/notification-filter';

@Injectable()
export class NotificationsDtoService implements DtoListService<
  NotificationDefinitionListDto,
  FilteredParams<NotificationDefinitionListDto, NotificationFilter>
> {
  getAll(
    param: GetAllRequestParameters<
      FilteredParams<NotificationDefinitionListDto, NotificationFilter>
    >,
  ): Observable<ListPaginatedDTO<NotificationDefinitionListDto>> {
    return from(
      listNotificationDefinitionsAsync({
        body: {
          pagination: convertPaginationParams(param.pagination),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          sort: mapSort(param.parameters?.sort) as any,
          searchText: param.parameters?.filter?.searchText,
          status: param.parameters?.filter?.status,
          language: param.parameters?.filter?.language,
          types: param.parameters?.filter?.types ?? [],
        },
      }).then(resp => resp.data),
    );
  }

  getById(id: string): Observable<NotificationDefinitionGetDto> {
    return from(findNotificationDefinitionAsync({ path: { id } }).then(x => x.data));
  }

  create(_: NotificationDefinitionPutDto): Observable<unknown> {
    // Will be implemented soon.
    return of({}).pipe(delay(1000));
  }

  update(id: string, dto: NotificationDefinitionPutDto): Observable<unknown> {
    return from(updateNotificationDefinitionAsync({ path: { id }, body: dto }));
  }

  getDtoId(dto: NotificationDefinitionListDto): string;
  getDtoId(dto: NotificationDefinitionListDto | null | undefined): string | null | undefined;
  getDtoId(
    dto: Partial<NotificationDefinitionListDto> | null | undefined,
  ): string | null | undefined;
  getDtoId(
    dto: NotificationDefinitionListDto | Partial<NotificationDefinitionListDto> | null | undefined,
  ): string | null | undefined {
    if (isNil(dto)) {
      return dto;
    }
    return dto.id;
  }

  toString(dto: NotificationDefinitionListDto): string {
    return dto.title ?? UNKNOWN_ITEM;
  }

  delete(id: string): Observable<void> {
    return from(
      deleteAdminNewsAsync({ path: { id } }).then(() => {
        // type cast to void
      }),
    );
  }
}
